/**
 * 函数防抖
 * @export
 * @template Result
 * @param {() => Result} func
 * @param {number} delay
 * @param {boolean} [imemediate]
 * @returns {() => Result}
 */
export function debounce<Result extends any = any>(
  func: () => Result,
  delay: number,
  imemediate?: boolean
): () => Result {
  let timer = null,
    result: Result = null,
    timestamp = 0,
    context: any,
    args: IArguments

  const later = function () {
    const last = +new Date() - timestamp

    if (last < delay && last >= 0) {
      timer = setTimeout(later, delay - last)
    } else {
      timer = null
      if (!imemediate) {
        result = func.apply(context, args)
        if (!timer) context = args = null
      }
    }
  }

  return function () {
    context = this
    args = arguments
    timestamp = +new Date()

    const callNow = imemediate && !timer

    if (!timer) timer = setTimeout(later, delay)

    if (callNow) {
      result = func.apply(context, args)
      context = args = null
    }

    return result
  }
}

/**
 *  节流函数，返回函数连续调用时，func 执行频率限定为 次 / delay
 *
 * @param  {function}   func      回调函数
 * @param  {number}     delay      表示时间窗口的间隔
 * @param  {object}     options   如果想忽略开始函数的的调用，传入{leading: false}。
 *                                如果想忽略结尾函数的调用，传入{trailing: false}
 *                                两者不能共存，否则函数不能执行
 * @return {function}             返回客户调用函数
 */
export function throttle<Result extends any = any>(
  func: () => Result,
  delay: number,
  options: { leading?: boolean; trailing?: boolean }
): () => Result {
  let previous = 0,
    timer = null,
    result: Result,
    args: IArguments,
    context: any

  if (!options) {
    options = {}
  }

  const later = function () {
    previous = options.leading === false ? 0 : +new Date()
    timer = null
    result = func.apply(context, args)
    if (!timer) {
      context = args = null
    }
  }

  return function () {
    context = this
    args = arguments
    const now = +new Date()

    if (!previous && options.leading === false) {
      previous = now
    }

    let remaining = delay - (now - previous)

    if (remaining <= 0 || remaining > delay) {
      // 如果存在定时器就清理掉否则会调用二次回调
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      previous = now
      result = func.apply(context, args)
      if (!timer) context = args = null
    } else if (!timer && options.trailing !== false) {
      timer = setTimeout(later, remaining)
    }
    return result
  }
}

/**
 *
 * @export
 * @param {string} str
 */
export function copyToClipboard(str: string) {
  const el = document.createElement("textarea")
  el.value = str
  document.body.appendChild(el)
  el.select()
  document.execCommand("copy")
  document.body.removeChild(el)
}

export function reducer<T extends object>(
  accumulator: T,
  currentValue: string
) {
  let [key, val] = currentValue.split("=")

  key = key.trim()

  if (!key) {
    return accumulator
  }

  if (val) {
    val = val.trim()
  }

  accumulator[key] = val

  return accumulator
}

/**
 * 解析查询参数
 */

export function urlParse() {
  const hash: string = window.location.href
  let parsed = Object.create(null)

  if (!hash.split("?")[1]) {
    return parsed
  }
  return hash.split("?")[1].split("&").reduce(reducer, parsed)
}

export function parseQuery(qs: string) {
  let parsed = Object.create(null)

  if (qs) {
    return qs.split("?")[1].split("&").reduce(reducer, parsed)
  }
  return parsed
}

/**
 * @desc 深拷贝，支持常见类型
 * @param {Any} values
 */
export function deepClone(values: any) {
  let copy: any

  // Handle the 3 simple types, and null or undefined
  if (null == values || "object" != typeof values) return values

  // Handle Date
  if (values instanceof Date) {
    copy = new Date()
    copy.setTime(values.getTime())
    return copy
  }

  // Handle Array
  if (values instanceof Array) {
    copy = []
    for (var i = 0, len = values.length; i < len; i++) {
      copy[i] = deepClone(values[i])
    }
    return copy
  }

  // Handle Object
  if (values instanceof Object) {
    copy = {}
    for (var attr in values) {
      if (values.hasOwnProperty(attr)) copy[attr] = deepClone(values[attr])
    }
    return copy
  }

  throw new Error("Unable to copy values! Its type isn't supported.")
}

/**
 * 对象浅复制
 * @param {object} values
 * @returns
 */
export function shallowCopy(values: object) {
  let o = Object.create(null)

  for (const key in values) {
    o[key] = values[key]
  }

  return o
}

export function dataWithComma(data) {
  if (!data) {
    return 0
  }
  var reg = /(\d{1,3})(?=(\d{3})+(\.\d*)?$)/g
  var num = data + ""
  return num.replace(reg, "$1,")
}

export function isElementInViewport(el: HTMLElement) {
  var rect = el.getBoundingClientRect()
  return (
    rect.bottom > 0 &&
    rect.top < (window.innerHeight || document.documentElement.clientHeight)
  )
}

/**
 * 数字增加百分号
 * @param data
 */

export function toPercent(point) {
  if (point === 0) {
    return 0
  }
  var str = Number(point * 100).toFixed(2)
  str += "%"
  return str
}

export function downloadFile(content: Blob, fileName: string) {
  const eleLink = document.createElement("a")
  eleLink.download = fileName
  eleLink.style.display = "none"
  const href = window.URL.createObjectURL(content)
  eleLink.href = href
  document.body.appendChild(eleLink)
  eleLink.click()
  document.body.removeChild(eleLink)
  window.URL.revokeObjectURL(href)
}

export function uuid() {
  var s = []
  var hexDigits = "0123456789abcdef"
  for (var i = 0; i < 36; i++) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1)
  }
  s[14] = "4" // bits 12-15 of the time_hi_and_version field to 0010
  s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1) // bits 6-7 of the clock_seq_hi_and_reserved to 01
  s[8] = s[13] = s[18] = s[23] = "-"

  var uuid = s.join("")
  return uuid
}

/**
 *
 * @export
 * @param {number} number
 * @param {number} precision
 * @returns
 */
export function round(number: number, precision: number) {
  return (
    Math.round(((+number + "e" + precision) as unknown) as number) /
    Math.pow(10, precision)
  )
}

/**
 ** 加法函数，用来得到精确的加法结果
 ** 说明：javascript的加法结果会有误差，在两个浮点数相加的时候会比较明显。这个函数返回较为精确的加法结果。
 ** 调用：accAdd(arg1,arg2)
 ** 返回值：arg1加上arg2的精确结果
 **/
export function accAdd(arg1, arg2) {
  var r1, r2, m, c
  try {
    r1 = arg1.toString().split(".")[1].length
  } catch (e) {
    r1 = 0
  }
  try {
    r2 = arg2.toString().split(".")[1].length
  } catch (e) {
    r2 = 0
  }
  c = Math.abs(r1 - r2)
  m = Math.pow(10, Math.max(r1, r2))
  if (c > 0) {
    var cm = Math.pow(10, c)
    if (r1 > r2) {
      arg1 = Number(arg1.toString().replace(".", ""))
      arg2 = Number(arg2.toString().replace(".", "")) * cm
    } else {
      arg1 = Number(arg1.toString().replace(".", "")) * cm
      arg2 = Number(arg2.toString().replace(".", ""))
    }
  } else {
    arg1 = Number(arg1.toString().replace(".", ""))
    arg2 = Number(arg2.toString().replace(".", ""))
  }
  return (arg1 + arg2) / m
}

/**
 ** 减法函数，用来得到精确的减法结果
 ** 说明：javascript的减法结果会有误差，在两个浮点数相减的时候会比较明显。这个函数返回较为精确的减法结果。
 ** 调用：accSub(arg1,arg2)
 ** 返回值：arg1加上arg2的精确结果
 **/
export function accSub(arg1, arg2) {
  var r1, r2, m, n
  try {
    r1 = arg1.toString().split(".")[1].length
  } catch (e) {
    r1 = 0
  }
  try {
    r2 = arg2.toString().split(".")[1].length
  } catch (e) {
    r2 = 0
  }
  m = Math.pow(10, Math.max(r1, r2)) //last modify by deeka //动态控制精度长度
  n = r1 >= r2 ? r1 : r2
  return Number(((arg1 * m - arg2 * m) / m).toFixed(n))
}

/**
 ** 乘法函数，用来得到精确的乘法结果
 ** 说明：javascript的乘法结果会有误差，在两个浮点数相乘的时候会比较明显。这个函数返回较为精确的乘法结果。
 ** 调用：accMul(arg1,arg2)
 ** 返回值：arg1乘以 arg2的精确结果
 **/
export function accMul(arg1, arg2) {
  var m = 0,
    s1 = arg1.toString(),
    s2 = arg2.toString()
  try {
    m += s1.split(".")[1].length
  } catch (e) {}
  try {
    m += s2.split(".")[1].length
  } catch (e) {}
  return (
    (Number(s1.replace(".", "")) * Number(s2.replace(".", ""))) /
    Math.pow(10, m)
  )
}

/**
 ** 除法函数，用来得到精确的除法结果
 ** 说明：javascript的除法结果会有误差，在两个浮点数相除的时候会比较明显。这个函数返回较为精确的除法结果。
 ** 调用：accDiv(arg1,arg2)
 ** 返回值：arg1除以arg2的精确结果
 **/
export function accDiv(arg1, arg2) {
  var t1 = 0,
    t2 = 0,
    r1,
    r2
  try {
    t1 = arg1.toString().split(".")[1].length
  } catch (e) {}
  try {
    t2 = arg2.toString().split(".")[1].length
  } catch (e) {}
  r1 = Number(arg1.toString().replace(".", ""))
  r2 = Number(arg2.toString().replace(".", ""))
  return (r1 / r2) * Math.pow(10, t2 - t1)
}

const utils = {
  debounce,
  throttle,
  copyToClipboard,
  urlParse,
  parseQuery,
  shallowCopy,
  deepClone,
  dataWithComma,
  isElementInViewport,
  toPercent,
  downloadFile,
  uuid,
  round,
  accAdd,
  accSub,
  accMul,
  accDiv,
}

"Boolean|Number|String|Function|Array|Date|RegExp|Object|Error"
  .split("|")
  .forEach(function (type) {
    utils[`is${type}`] = function (obj: any) {
      return {}.toString.call(obj) === `[object ${type}]`
    }
  })

export default utils
